/**
 * RepatriationReport.js — the Cash Owed view.
 *
 * Brian, 21 September 2026: *"the cash repatriation report is so good that it
 * should actually be its own tab under NAV/Performance"* — so this draws in
 * two places, the report centre and the Cash Owed page, out of one file. It
 * reads `GET /reports/cash-repatriation/` and renders it; it fetches nothing,
 * so each caller decides the as-of date it asks for.
 *
 * Brian, 7 October 2026: *"cash owed is whenever we generate proceeds that are
 * directly attributable to a deal. Whether thats trading proceeds, dividends,
 * interest (cash), redemption proceeds, etc. Thats what the sheet should show.
 * Not just cash moved from prime brokers to bank accounts"* (D-251).
 *
 * Brian, 27 October 2026, twice. First: *"this view needs massive
 * improvement"*, and he picked four lines that answer first. Then, of the
 * company list under them: *"I need to see a matrix of deals on the x or y
 * axis and then the investors on the other axis and then sum columns"*. So:
 *
 *   1. Four lines: what the deals owe, what has already been wired and not
 *      posted, the range that leaves, and the cash to pay it.
 *   2. The matrix. A deal per row, an investor per column, each cell that
 *      investor's share of what the deal owes (`owed_to`, split in
 *      `cash_owed.split_owed`: capital back first pro rata, then 80/15/5).
 *      Each row adds to the deal; the columns add to what each investor is
 *      owed; under them, the wires already sent to each, and what is left.
 *   3. Where the cash is and the krona at the broker, folded below.
 *
 * Deals are named by company and instrument, never the ticker their record is
 * filed under (§0.2). Adam Rabie and Paradox SPC Fund are one lender (§0.46)
 * and share a column.
 */

import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { C, FONT, figure, Banner, Grid, td, tdR, th, thR } from "./reportUi";
import { money, day, amount, DASH } from "./format";
import { ExportCsvButton, InfoTip, PdfButton, ToolRow } from "./PageTools";
import { dealReportPath } from "./links";
import "./RepatriationReport.css";

const DUST = 1;

const n = (value) =>
  value === null || value === undefined || value === "" || Number.isNaN(Number(value))
    ? null
    : Number(value);

const whole = (value) => money(value, { whole: true });

const companyOf = (row) => row.company || row.issuer || row.deal;
const instrumentOf = (row) => row.instrument || row.deal;

/* ─── Shared bits ─────────────────────────────────────────────────── */

const muted = { color: C.muted, fontSize: 11.5 };

function Caret({ open }) {
  return (
    <span aria-hidden="true" style={{
      display: "inline-block", width: 12, color: C.muted, fontSize: 10,
      transform: open ? "rotate(90deg)" : "none", transition: "transform .12s",
    }}>
      ▶
    </span>
  );
}

/** A folded section: its name and its figure on the fold, the rest inside. */
function Fold({ id, title, figureText, meta, open, onToggle, tip, children, foldRef }) {
  return (
    <section id={id} ref={foldRef} style={{
      border: `1px solid ${C.borderLight}`, borderRadius: 6, background: C.card,
      marginBottom: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px" }}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          style={{
            display: "flex", alignItems: "baseline", gap: 8, flex: 1, minWidth: 0,
            border: "none", background: "none", padding: 0, cursor: "pointer",
            textAlign: "left", fontFamily: FONT.body, color: C.text,
          }}
        >
          <Caret open={open} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
          {meta && <span style={{ ...muted, marginLeft: 4 }}>{meta}</span>}
        </button>
        {tip && <InfoTip align="right" wide>{tip}</InfoTip>}
        <span style={{ ...figure, fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap" }}>
          {figureText}
        </span>
      </div>
      {open && <div style={{ padding: "0 14px 14px" }}>{children}</div>}
    </section>
  );
}

/* ─── 1. The answer ───────────────────────────────────────────────── */

function SummaryLine({ label, value, note, strong, tone }) {
  const ink = tone === "bad" ? C.red : tone === "good" ? C.green : C.text;
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 18, rowGap: 2,
      padding: "9px 0", borderBottom: `1px solid ${C.borderLight}`,
    }}>
      <span style={{
        flex: "0 0 210px", fontSize: 13, color: strong ? C.text : C.secondary,
        fontWeight: strong ? 700 : 500,
      }}>
        {label}
      </span>
      <span style={{
        ...figure, flex: "0 0 auto", minWidth: 200, fontSize: strong ? 20 : 16,
        fontWeight: strong ? 600 : 500, color: ink,
      }}>
        {value}
      </span>
      {note && <span style={{ flex: "1 1 260px", fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{note}</span>}
    </div>
  );
}

function Summary({ dollars, currencies, totals }) {
  const wireCount = Number(dollars.open_payee_wires_count || 0);
  const breaks = Number(dollars.known_breaks_count || 0);
  const headroom = n(dollars.headroom_usd);
  const atBroker = currencies.filter((c) => n(c.held));
  const brokerUsd = n(totals.usd);

  const brokerNote = atBroker.length === 0
    ? "in the bank, distributable"
    : brokerUsd !== null && totals.usd_is_complete
    ? <>in the bank, distributable · plus <strong style={{ ...figure, color: C.secondary }}>{whole(brokerUsd)}</strong> of {atBroker.map((c) => c.currency).join(" and ")} at the broker if brought home</>
    : <>in the bank, distributable · plus {atBroker.map((c) => money(c.held, { currency: c.currency, whole: true })).join(", ")} at the broker, not all of it in dollars (no usable fixing)</>;

  return (
    <div style={{ marginBottom: 18 }}>
      <SummaryLine label="Cash to pay it" value={whole(dollars.available_usd)} note={brokerNote} strong />
      {headroom !== null && (
        <SummaryLine
          label="Left over"
          value={whole(headroom)}
          tone={headroom >= 0 ? "good" : "bad"}
          note={
            n(dollars.headroom_usd_if_swept) !== null
              ? `${whole(dollars.headroom_usd_if_swept)} if the foreign cash is brought home`
              : "cash in the bank less what the deals owe, known breaks left out"
          }
        />
      )}
      {breaks > 0 && (
        <SummaryLine
          label="Known breaks"
          value={whole(dollars.known_breaks_usd)}
          note={`${breaks} ${breaks === 1 ? "deal" : "deals"} in the totals below, outstanding and not actionable`}
        />
      )}
      {headroom === null && dollars.headroom_withheld_because && wireCount > 0 && (
        <div style={{ ...muted, marginTop: 8 }}>
          No left-over figure until the wires already sent are posted to their deals: they are
          out of the bank and still in what the deals owe, so it would count them twice.
        </div>
      )}
      {headroom === null && dollars.headroom_withheld_because && wireCount === 0 && (
        <div style={{ ...muted, marginTop: 8 }}>{dollars.headroom_withheld_because}</div>
      )}
    </div>
  );
}

/* ─── A deal unfolded ─────────────────────────────────────────────── */

const KIND_ORDER = ["trading", "redemptions", "interest", "dividends", "fees",
                    "costs_reimbursed", "other"];

function DetailLine({ label, value, children }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", columnGap: 14, rowGap: 2, padding: "3px 0" }}>
      <span style={{ flex: "0 0 96px", fontSize: 11.5, color: C.muted }}>{label}</span>
      {value !== undefined && (
        <span style={{ ...figure, flex: "0 0 120px", textAlign: "right", fontSize: 12.5, color: C.text }}>
          {value}
        </span>
      )}
      <span style={{ flex: "1 1 260px", fontSize: 11.5, color: C.secondary, lineHeight: 1.5 }}>{children}</span>
    </div>
  );
}

const parts = (pairs) =>
  pairs
    .filter(([, v]) => n(v) !== null && Math.abs(n(v)) >= DUST)
    .map(([label, v], i) => (
      <span key={label}>
        {i > 0 && <span style={{ color: C.muted }}> · </span>}
        {label} <span style={figure}>{whole(v)}</span>
      </span>
    ));

function DealDetail({ row, labels }) {
  const by = row.generated_by_kind || {};
  const paidOut = (n(row.paid_usd) || 0) + (n(row.recycled_usd) || 0) + (n(row.kept_usd) || 0);
  const owed = n(row.owed_usd);
  return (
    <div style={{
      margin: "2px 0 6px", padding: "10px 14px", background: C.bg,
      border: `1px solid ${C.borderLight}`, borderRadius: 4,
    }}>
      <DetailLine label="Generated" value={whole(row.generated_usd)}>
        {parts(KIND_ORDER.map((k) => [labels[k] || k, by[k]]))}
      </DetailLine>
      <DetailLine label="Put back in" value={whole(row.reinvested_usd)}>
        spent on the deal itself
      </DetailLine>
      <DetailLine label="Paid out" value={whole(paidOut)}>
        {parts([
          ["to payees", row.paid_usd],
          ["recycled into other deals", row.recycled_usd],
          ["Helena Partners Inc's 5%", row.kept_usd],
        ])}
      </DetailLine>
      <DetailLine label="Still owed" value={whole(owed)}>
        {row.owed_since
          ? <>since {day(row.owed_since)}{n(row.days_owed) !== null && <> · <strong>{row.days_owed} days</strong></>}</>
          : owed !== null && owed <= -DUST ? row.reads_as : null}
      </DetailLine>
      <DetailLine label="Lenders">
        {(row.lenders || []).length ? row.lenders.join(", ") : "no lenders' notes on this record"}
      </DetailLine>
      {row.known_break && (
        <DetailLine label="Known break">
          <span style={{ color: C.gold }}>
            {row.known_break.reason}
            {row.known_break.waiting_on && <> · waiting on <strong>{row.known_break.waiting_on}</strong></>}
            {row.known_break.since && <> · since {day(row.known_break.since)}</>}
          </span>
        </DetailLine>
      )}
      <DetailLine label="Record">
        filed as {row.deal}
        {row.funded_on && <>, funded {day(row.funded_on)}</>}
        {row.deal_id != null && (
          <> · <Link to={dealReportPath(row.deal_id)} style={{ color: C.accent, fontWeight: 600 }}>open the deal report</Link></>
        )}
      </DetailLine>
    </div>
  );
}

const BreakTag = () => (
  <span style={{
    marginLeft: 8, fontSize: 9.5, fontWeight: 700, letterSpacing: ".06em",
    textTransform: "uppercase", color: C.gold, border: `1px solid ${C.gold}66`,
    borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap",
  }}>
    known break
  </span>
);

/* ─── 2. The matrix ───────────────────────────────────────────────── */

// One lender under two names (§0.46): a distribution to one is a
// distribution to the other, so they are one column.
const ONE_LENDER = {
  "Adam Rabie": "Adam Rabie / Paradox",
  "Paradox SPC Fund": "Adam Rabie / Paradox",
};
// The three that are not lenders, after the lenders, in this order.
const TAIL = ["Deal team", "Helena Partners Inc", "HPI and its lenders, not split"];
const investorOf = (name) => ONE_LENDER[name] || name;

/** The matrix out of the deal rows: a row per deal owing anything, a column
    per investor, the column totals, and the open wires by the same columns. */
export function buildMatrix(deals, wires) {
  const rows = deals
    .filter((d) => n(d.owed_usd) !== null && Math.abs(n(d.owed_usd)) >= DUST)
    .map((d) => {
      const cells = {};
      Object.entries(d.owed_to || {}).forEach(([who, v]) => {
        const k = investorOf(who);
        cells[k] = (cells[k] || 0) + (n(v) || 0);
      });
      return { deal: d, cells, total: n(d.owed_usd) };
    });
  const totals = {};
  rows.forEach((r) => Object.entries(r.cells).forEach(([k, v]) => {
    totals[k] = (totals[k] || 0) + v;
  }));
  const wired = {};
  (wires || []).forEach((w) => {
    const k = investorOf(w.payee);
    wired[k] = (wired[k] || 0) + (n(w.amount) || 0);
  });
  const lenders = [...new Set([...Object.keys(totals), ...Object.keys(wired)])]
    .filter((k) => !TAIL.includes(k))
    .sort((a, b) => (totals[b] || 0) - (totals[a] || 0) || a.localeCompare(b));
  return {
    rows,
    columns: [...lenders, ...TAIL.filter((k) => k in totals)],
    totals,
    wired,
    total: rows.reduce((s, r) => s + r.total, 0),
    atNil: deals.filter((d) => n(d.owed_usd) !== null && Math.abs(n(d.owed_usd)) < DUST).length,
  };
}

function matches(row, query) {
  if (!query) return true;
  const hay = [companyOf(row), row.issuer, row.deal, row.instrument,
               ...Object.keys(row.owed_to || {}), ...(row.lenders || [])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

/** A cell: whole dollars, brackets for a negative, blank for nothing. */
const cellText = (v) => (!v || Math.abs(v) < 0.5 ? "" : money(v, { whole: true, symbol: false }));

// The deal and its total stay put while the investors scroll under them.
const DEAL_W = 210;
const stickyLeft = (bg) => ({
  position: "sticky", left: 0, zIndex: 1, background: bg,
  width: DEAL_W, minWidth: DEAL_W, maxWidth: DEAL_W, boxSizing: "border-box",
});
const stickyTotal = (bg) => ({
  position: "sticky", left: DEAL_W, zIndex: 1, background: bg,
  borderRight: `1px solid ${C.border}`,
});

const numCell = { ...tdR, padding: "6px 8px", whiteSpace: "nowrap", fontSize: 12 };

function Matrix({ deals, dollars, labels, methodTip }) {
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [openDeal, setOpenDeal] = useState(null);
  const m = useMemo(() => buildMatrix(deals, dollars.open_payee_wires), [deals, dollars]);
  const byName = useMemo(() => {
    const out = new Map();
    deals.forEach((d) => out.set(d.deal, d));
    return out;
  }, [deals]);

  const shown = m.rows
    .filter((r) => matches(r.deal, query.trim()))
    .sort((a, b) => (sortBy ? (b.cells[sortBy] || 0) - (a.cells[sortBy] || 0) : 0) || b.total - a.total);
  const filtering = shown.length !== m.rows.length;
  const sums = {};
  shown.forEach((r) => Object.entries(r.cells).forEach(([k, v]) => { sums[k] = (sums[k] || 0) + v; }));
  const shownTotal = shown.reduce((s, r) => s + r.total, 0);
  const wiredTotal = Object.values(m.wired).reduce((s, v) => s + v, 0);
  const span = m.columns.length + 2;

  const dealName = (d) => `${companyOf(d)} · ${instrumentOf(d)}`;
  const csvColumns = [
    { label: "Deal", value: (r) => r.label },
    ...m.columns.map((k) => ({ label: k, value: (r) => (r.cells[k] ? r.cells[k].toFixed(2) : "") })),
    { label: "Total", value: (r) => r.total.toFixed(2) },
  ];
  const csvRows = [
    ...shown.map((r) => ({ label: dealName(r.deal), cells: r.cells, total: r.total })),
    { label: filtering ? "Shown" : "Total", cells: sums, total: shownTotal },
    ...(filtering || !wiredTotal ? [] : [
      { label: "Less wires already sent", cells: Object.fromEntries(Object.entries(m.wired).map(([k, v]) => [k, -v])), total: -wiredTotal },
      { label: "Owed after the wires", cells: Object.fromEntries(m.columns.map((k) => [k, (m.totals[k] || 0) - (m.wired[k] || 0)])), total: m.total - wiredTotal },
    ]),
  ];

  const head = (label, key, extra = {}) => (
    <th key={label} className={extra.position ? "co-total" : undefined}
        style={{ ...thR, whiteSpace: "normal", verticalAlign: "bottom", minWidth: 80, lineHeight: 1.3, padding: "0 8px 7px", ...extra }}>
      <button
        type="button"
        onClick={() => setSortBy(sortBy === key ? null : key)}
        title={`Sort by ${label}`}
        style={{
          border: "none", background: "none", padding: 0, cursor: "pointer", textAlign: "right",
          font: "inherit", color: sortBy === key ? C.text : "inherit", textTransform: "inherit",
          letterSpacing: "inherit",
        }}
      >
        {label}{sortBy === key ? " ▾" : ""}
      </button>
    </th>
  );

  const footRow = (label, cells, total, opts = {}) => {
    const bg = opts.bg || C.card;
    const rules = {
      background: bg,
      borderTop: opts.rule ? `1px solid ${C.border}` : undefined,
      borderBottom: opts.last ? `2px solid ${C.border}` : `1px solid ${C.borderLight}`,
    };
    return (
    <tr>
      <td className="co-deal" style={{ ...td, ...stickyLeft(bg), ...rules, fontWeight: 700 }}>
        {label}
      </td>
      <td className="co-total" style={{ ...numCell, ...stickyTotal(bg), ...rules, fontWeight: 700, color: total < 0 ? C.red : C.text }}>
        {cellText(total)}
      </td>
      {m.columns.map((k) => (
        <td key={k} style={{ ...numCell, ...rules, fontWeight: 600, color: (cells[k] || 0) < 0 ? C.red : C.text }}>
          {cellText(cells[k])}
        </td>
      ))}
    </tr>
    );
  };

  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search deal or investor"
          aria-label="Search deal or investor"
          className="no-print"
          style={{
            flex: "1 1 220px", maxWidth: 300, padding: "7px 10px", fontSize: 12.5,
            border: `1px solid ${C.border}`, borderRadius: 5, background: C.inputBg,
            color: C.text, fontFamily: FONT.body,
          }}
        />
        <span style={muted}>
          {filtering ? `${shown.length} of ${m.rows.length}` : m.rows.length} deals
          {m.atNil > 0 && ` · ${m.atNil} at nil not shown`} · USD
        </span>
        <span style={{ marginLeft: "auto" }}>
          <ToolRow>
            <InfoTip align="right" wide>{methodTip}</InfoTip>
            <ExportCsvButton filename="cash-owed-by-investor" columns={csvColumns} rows={csvRows} />
            <PdfButton />
          </ToolRow>
        </span>
      </div>

      <div style={{
        overflow: "auto", maxHeight: "78vh", border: `1px solid ${C.borderLight}`,
        borderRadius: 6, background: C.card,
      }}>
        <table className="co-matrix" style={{ borderCollapse: "separate", borderSpacing: 0, fontFamily: FONT.body, minWidth: "100%" }}>
          {/* The sums sit under the header and stay there while the deals
              scroll: the totals are what the page is for. */}
          <thead style={{ position: "sticky", top: 0, zIndex: 4 }}>
            <tr>
              <th className="co-deal" style={{ ...th, ...stickyLeft(C.card), zIndex: 3, verticalAlign: "bottom" }}>Deal</th>
              {head("Total", null, { ...stickyTotal(C.card), zIndex: 3 })}
              {m.columns.map((k) => head(k, k))}
            </tr>
            {footRow(filtering ? "Shown" : "Total", sums, shownTotal, { rule: true, bg: C.bg, last: filtering || !wiredTotal })}
            {!filtering && wiredTotal > 0 && (
              <>
                {footRow(
                  "Less wires already sent",
                  Object.fromEntries(Object.entries(m.wired).map(([k, v]) => [k, -v])),
                  -wiredTotal,
                )}
                {footRow(
                  "Owed after the wires",
                  Object.fromEntries(m.columns.map((k) => [k, (m.totals[k] || 0) - (m.wired[k] || 0)])),
                  m.total - wiredTotal,
                  { rule: true, bg: C.bg, last: true },
                )}
              </>
            )}
          </thead>
          <tbody>
            {shown.map((r) => {
              const d = r.deal;
              const id = d.deal_id ?? d.deal;
              const isOpen = openDeal === id;
              const from = (d.owed_to_from || []).map((name) => {
                const src = byName.get(name);
                return src ? instrumentOf(src) : name;
              });
              const fromText = from.length === 1
                ? `lenders of the ${from[0]}`
                : "lenders of the company's other deals";
              return (
                <Fragment key={id}>
                  <tr style={{ background: isOpen ? C.bg : undefined }}>
                    <td className="co-deal" style={{ ...td, ...stickyLeft(isOpen ? C.bg : C.card), padding: "6px 10px" }}>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? "Hide" : "Show"} ${dealName(d)}`}
                        onClick={() => setOpenDeal(isOpen ? null : id)}
                        style={{
                          border: "none", background: "none", padding: 0, cursor: "pointer",
                          textAlign: "left", fontFamily: FONT.body, color: C.text, display: "block",
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 12.5 }}>{companyOf(d)}</span>
                        {d.known_break && <BreakTag />}
                        <span style={{ display: "block", ...muted, fontSize: 11 }}>
                          {instrumentOf(d)}
                          {from.length > 0 && <span title={`Its lenders' notes sit on ${from.join(", ")}`}> · {fromText}</span>}
                        </span>
                      </button>
                    </td>
                    <td className="co-total" style={{ ...numCell, ...stickyTotal(isOpen ? C.bg : C.card), fontWeight: 600, color: r.total < 0 ? C.red : C.text }}>
                      {cellText(r.total)}
                    </td>
                    {m.columns.map((k) => (
                      <td key={k} style={{ ...numCell, background: isOpen ? C.bg : C.card, color: (r.cells[k] || 0) < 0 ? C.red : C.secondary }}>
                        {cellText(r.cells[k])}
                      </td>
                    ))}
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={span} style={{ ...td, paddingTop: 0, background: C.bg }}>
                        <div style={{ position: "sticky", left: 10, maxWidth: 880 }}>
                          <DealDetail row={d} labels={labels} />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {shown.length === 0 && (
              <tr><td colSpan={span} style={{ ...td, ...muted, padding: 16 }}>Nothing matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── 3. The folds ────────────────────────────────────────────────── */

function CashParts({ dollars }) {
  const parts = [
    ["Bank", "dollars", dollars.available_usd, true],
    ["Bank", "other currencies", dollars.bank_other_ccy_usd, false],
    ["Broker", "dollars", dollars.broker_cash_usd, false],
    ["Broker", "other currencies", dollars.broker_other_ccy_usd, false],
  ];
  const accounts = (dollars.accounts || []).filter((a) => Math.abs(n(a.balance) || 0) >= DUST);
  const line = { display: "flex", alignItems: "baseline", gap: 10, padding: "6px 0", borderBottom: `1px solid ${C.borderLight}` };
  return (
    <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "flex-start" }}>
      <div style={{ flex: "1 1 360px", minWidth: 280 }}>
        {dollars.dashboard_unavailable_because && (
          <Banner tone="bad" title="Total withheld.">{dollars.dashboard_unavailable_because}</Banner>
        )}
        {parts.map(([group, kind, value, lead]) => (
          <div key={`${group} ${kind}`} style={line}>
            <span style={{ fontSize: 12.5, color: lead ? C.text : C.secondary, fontWeight: lead ? 600 : 400 }}>
              {group} <span style={{ color: C.muted }}>· {kind}</span>
              {lead && (
                <span style={{ marginLeft: 8, fontSize: 9.5, letterSpacing: ".07em", textTransform: "uppercase", color: C.accent, fontWeight: 700 }}>
                  distributable
                </span>
              )}
            </span>
            <span style={{ ...figure, marginLeft: "auto", fontSize: 12.5 }}>{whole(value)}</span>
          </div>
        ))}
        <div style={{ ...line, borderBottom: "none", borderTop: `2px solid ${C.border}` }}>
          <span style={{ fontSize: 12.5, fontWeight: 700 }}>Dashboard &ldquo;Fund cash&rdquo;</span>
          <span style={{ ...figure, marginLeft: "auto", fontSize: 13, fontWeight: 600 }}>
            {whole(dollars.dashboard_fund_cash_usd)}
          </span>
        </div>
      </div>
      <div style={{ flex: "1 1 260px", minWidth: 240 }}>
        <div style={{ ...muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", fontSize: 10, marginBottom: 4 }}>
          The distributable dollars
        </div>
        {accounts.map((a) => (
          <div key={`${a.source} ${a.account}`} style={line}>
            <span style={{ fontSize: 12, color: C.secondary }}>{a.account} <span style={{ color: C.muted }}>· {a.source}</span></span>
            <span style={{ ...figure, marginLeft: "auto", fontSize: 12 }}>{whole(a.balance)}</span>
          </div>
        ))}
        {accounts.length === 0 && <div style={muted}>no dollar account holds anything</div>}
        {(dollars.other_currency_accounts || []).length > 0 && (
          <div style={{ marginTop: 10 }}>
            <Banner tone="warn" title="Not counted in the dollar figure:">
              {dollars.other_currency_accounts
                .map((a) => `${a.account} ${money(a.balance, { currency: a.currency })}`)
                .join(" · ")}
            </Banner>
          </div>
        )}
      </div>
    </div>
  );
}

const rateOf = (value) => (n(value) === null ? DASH : n(value).toFixed(4));

/** Where today's fixing sits in the window, as a fact and not a verdict.
    `percentile_of_window` is the share of fixings at or below today's. */
function rateVsWindow(c) {
  const p = n(c.percentile_of_window);
  const of = `of the last ${c.observations} fixings`;
  return p < 0.5
    ? `${c.currency} buys fewer dollars today than on ${Math.round((1 - p) * 100)}% ${of}`
    : `${c.currency} buys more dollars today than on ${Math.round(p * 100)}% ${of}`;
}

function Broker({ currencies, nameOf }) {
  return currencies.map((c) => (
    <div key={c.currency} style={{ marginBottom: 14 }}>
      {c.usd_unavailable_because && (
        <Banner tone="bad" title="Not converted.">{c.usd_unavailable_because}</Banner>
      )}
      <div style={{ fontSize: 12.5, color: C.secondary, lineHeight: 1.7, marginBottom: 8 }}>
        <strong style={{ ...figure, color: C.text }}>{money(c.held, { currency: c.currency })}</strong>
        {n(c.usd) !== null ? (
          <> is <strong style={{ ...figure, color: C.text }}>{whole(c.usd)}</strong> at {rateOf(c.usd_per_unit)} ({day(c.rate_date)})</>
        ) : (
          <> has no usable fixing, so no dollar figure</>
        )}
        {n(c.percentile_of_window) !== null && (
          <>
            {" "}· {rateVsWindow(c)}
            <InfoTip>{c.reads_as}</InfoTip>
          </>
        )}
        {n(c.usd_vs_window_median) !== null && (
          <> · {whole(Math.abs(n(c.usd_vs_window_median)))} {n(c.usd_vs_window_median) < 0 ? "less" : "more"} than at the median rate</>
        )}
        {n(c.usd_per_one_pct_move) !== null && <> · a 1% move is {whole(c.usd_per_one_pct_move)}</>}
        {c.oldest_unswept && <> · there since {day(c.oldest_unswept)}</>}
      </div>
      {c.action && <Banner tone={/sweep/.test(c.action) ? "good" : "warn"} title="Against the threshold you passed:">{c.action}</Banner>}
      <Grid
        minWidth={520}
        headers={["Deal", { name: `Held (${c.currency})`, right: true }, "Last movement",
                  { name: "Days idle", right: true }]}
      >
        {(c.positions || []).map((p, i) => (
          <tr key={`${p.deal_id ?? p.deal}-${i}`}>
            <td style={td}>{nameOf(p)}</td>
            <td style={tdR}>{amount(p.held, 2)}</td>
            <td style={{ ...td, color: C.muted, whiteSpace: "nowrap" }}>{day(p.last_movement)}</td>
            <td style={tdR}>{p.days_since_last_movement ?? DASH}</td>
          </tr>
        ))}
      </Grid>
    </div>
  ));
}

function OutOfPocket({ rows, nameOf }) {
  return (
    <>
      <div style={{ ...muted, marginBottom: 8 }}>
        Two facts side by side, not one conclusion: a bank account on the deal is below zero in
        dollars, and the deal&rsquo;s own foreign proceeds have not come home.
      </div>
      <Grid minWidth={560} headers={["Deal", "Bank account", { name: "Bank balance", right: true }, "Broker holds"]}>
        {rows.map((row, i) => (
          <tr key={i}>
            <td style={td}>{nameOf(row)}</td>
            <td style={{ ...td, color: C.muted }}>{String(row.bank_account || "").replace(/^Assets:Cash:/, "")}</td>
            <td style={{ ...tdR, color: C.red }}>{whole(row.bank_balance_usd)}</td>
            <td style={td}>
              {(row.broker_holds || []).map((h) => money(h.held, { currency: h.currency, whole: true })).join(" · ") || DASH}
            </td>
          </tr>
        ))}
      </Grid>
    </>
  );
}

/* ─── The page ────────────────────────────────────────────────────── */

function RepatriationView({ report }) {
  const currencies = report.currencies || [];
  const outOfPocket = report.bank_out_of_pocket || [];
  const totals = report.totals || {};
  const dollars = report.dollars;
  const unpriced = totals.currencies_unpriced || [];
  const deals = useMemo(() => (dollars && dollars.owed_deals) || [], [dollars]);

  const [open, setOpen] = useState(() => new Set());
  const flip = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const labels = useMemo(() => {
    const out = {};
    ((dollars && dollars.owed_kinds) || []).forEach((k) => { out[k.key] = k.label; });
    return out;
  }, [dollars]);
  const notInDollars = deals.filter((r) => n(r.owed_usd) === null);

  // The broker's and the bank's rows name a deal by its record; say it the
  // way the owed table does, company first.
  const byId = useMemo(() => {
    const m = new Map();
    deals.forEach((d) => { if (d.deal_id != null) m.set(d.deal_id, d); });
    return m;
  }, [deals]);
  const nameOf = (row) => {
    const d = row.deal_id != null ? byId.get(row.deal_id) : null;
    return d ? `${companyOf(d)} · ${instrumentOf(d)}` : row.deal;
  };

  const methodTip = dollars && (
    <>
      <p style={{ margin: "0 0 6px" }}>{dollars.owed_reads_as}</p>
      <p style={{ margin: "0 0 6px" }}>Cash is {dollars.scope}</p>
      {Number(dollars.open_payee_wires_count || 0) > 0 && (
        <p style={{ margin: "0 0 6px" }}>{dollars.open_wires_reads_as}</p>
      )}
      {Number(dollars.known_breaks_count || 0) > 0 && dollars.known_breaks_reads_as && (
        <p style={{ margin: "0 0 6px" }}>Known breaks are {dollars.known_breaks_reads_as}.</p>
      )}
      <p style={{ margin: "0 0 6px" }}>
        Each cell is that investor&rsquo;s share of what the deal owes, on the house terms:
        capital back first, pro rata to each lender&rsquo;s capital in the deal; then profit
        80% to the lenders, 15% to the deal team (Jeremy Weech 40, Brian Coyne 40, Simon
        Tremblay 20) and 5% to Helena Partners Inc. A PPLN pays no promote. A deal whose
        notes sit on another record of the same company (an equity line beside its IPO) is
        split by that company&rsquo;s lenders, and says so under its name. Deals that pay
        promote differently are not modelled.
      </p>
      <p style={{ margin: "0 0 6px" }}>
        &ldquo;HPI and its lenders, not split&rdquo; is cash on deals with no lenders&rsquo;
        notes on any record of their company: held by Helena Partners Inc and the
        investors, in shares the book does not record.
      </p>
      <p style={{ margin: "0 0 6px" }}>
        The wires row takes each wire already sent off its payee&rsquo;s column whole. Whether
        any of Jeremy Weech&rsquo;s was promote rather than a lender distribution is open.
      </p>
      <p style={{ margin: "0 0 6px" }}>
        A row reading negative paid out more than this record shows generated, usually
        because a sibling record of the same company holds the proceeds.
      </p>
      <p style={{ margin: 0 }}>Counts nothing for an investment not yet made: the book carries no forward pipeline.</p>
    </>
  );

  const brokerUsd = n(totals.usd);
  const heldText = currencies.map((c) => money(c.held, { currency: c.currency, whole: true })).join(" · ");

  return (
    <div>
      {report.fx_is_stale && (
        <Banner tone="bad" title="At least one currency has no usable fixing.">
          A fixing more than ten days old is refused rather than used, so{" "}
          {unpriced.map((u) => money(u.held, { currency: u.currency })).join(", ")} is shown in its own
          currency with no dollar value. Run <code>fetch-fx</code>.
        </Banner>
      )}

      {dollars && dollars.unavailable_because && (
        <Banner tone="bad" title="The dollar balances could not be read:">{dollars.unavailable_because}</Banner>
      )}

      {dollars && !dollars.unavailable_because && (
        <>
          <Summary
            dollars={{ ...dollars, headroom_usd_if_swept: report.headroom_usd_if_swept }}
            currencies={currencies}
            totals={totals}
          />
          <Matrix deals={deals} dollars={dollars} labels={labels} methodTip={methodTip} />
          {notInDollars.length > 0 && (
            <Banner tone="bad" title="Not in any dollar figure:">
              {notInDollars.map((r, i) => (
                <span key={r.deal_id ?? r.deal}>
                  {i > 0 && " · "}
                  {companyOf(r)} {instrumentOf(r)} —{" "}
                  {Object.entries(r.generated_native || {})
                    .map(([code, v]) => money(v, { currency: code }))
                    .join(" · ") || "no proceeds"}
                  {r.unavailable_because && <> ({r.unavailable_because})</>}
                </span>
              ))}
            </Banner>
          )}
        </>
      )}

      {dollars && !dollars.unavailable_because && (
        <Fold
          id="cash"
          title="Where the fund's cash is"
          meta="Dashboard “Fund cash”"
          figureText={whole(dollars.dashboard_fund_cash_usd)}
          open={open.has("cash")}
          onToggle={() => flip("cash")}
          tip={<>{dollars.dashboard_reads_as}. {dollars.reconciles_to_nav || ""}</>}
        >
          <CashParts dollars={dollars} />
        </Fold>
      )}

      <Fold
        id="broker"
        title="Foreign cash at the broker"
        meta={currencies.length ? heldText : "none"}
        figureText={currencies.length === 0 ? DASH : brokerUsd !== null && totals.usd_is_complete ? whole(brokerUsd) : "no usable fixing"}
        open={open.has("broker")}
        onToggle={() => flip("broker")}
        tip={(report.notes || []).map((note, i) => <p key={i} style={{ margin: "0 0 6px" }}>{note}</p>)}
      >
        {currencies.length === 0 ? (
          <div style={muted}>
            Nothing to sweep: no deal clearing holds a foreign-currency balance as at {day(report.as_of)}.
          </div>
        ) : (
          <Broker currencies={currencies} nameOf={nameOf} />
        )}
      </Fold>

      {outOfPocket.length > 0 && (
        <Fold
          id="out-of-pocket"
          title="Dollar accounts below zero beside foreign cash at the broker"
          meta={`${outOfPocket.length} ${outOfPocket.length === 1 ? "account" : "accounts"}`}
          figureText=""
          open={open.has("out-of-pocket")}
          onToggle={() => flip("out-of-pocket")}
        >
          <OutOfPocket rows={outOfPocket} nameOf={nameOf} />
        </Fold>
      )}
    </div>
  );
}

export default RepatriationView;
